import React, { FC } from 'react';
import PropTypes from 'prop-types';
import { Helmet, HelmetProps } from 'react-helmet';
import { uniqBy } from 'lodash-es';
import { useStaticQuery, graphql } from 'gatsby';
import { useLocation } from '@reach/router';
import {
  getPageName,
  getHrefLangByLocale,
  getHrefLangByLang,
  renderPath,
} from 'shared-utils';
import { useLocale } from 'providers/locale-provider';

export interface SEOProps {
  title: string;
  description: string;
  meta?: HelmetProps['meta'][] | HelmetProps['meta'];
  link?: HelmetProps['link'][] | HelmetProps['link'];
  image?: { src: string; width: number; height: number };
  noIndex?: boolean;
}

const SEO: FC<SEOProps> = ({
  description,
  meta,
  title,
  link,
  image,
  noIndex,
}) => {
  if (noIndex === true) {
    meta = meta?.concat({ name: 'robots', content: 'noindex' });
  }
  const { pathname } = useLocation();
  const { locale } = useLocale();
  const { site, socialImage, allSitePage } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            siteUrl
            keywords
            langs
            author
          }
        }
        socialImage: file(relativePath: { eq: "og_web_sharing.png" }) {
          childImageSharp {
            resize(width: 1200, height: 630) {
              src
              height
              width
            }
          }
        }
        allSitePage {
          nodes {
            path
            fields {
              langs
            }
          }
        }
      }
    `,
  );
  const node = allSitePage.nodes.find(({ path }) => path === pathname) || {
    fields: { langs: [] },
  };
  const hrefLangLinks = node.fields.langs.map(lang => {
    const pageName = getPageName(node.path);
    const { siteUrl } = site.siteMetadata;
    const slug = renderPath(pageName, lang);
    return {
      rel: 'alternate',
      hreflang: getHrefLangByLang(lang),
      href: `${siteUrl}${slug}`,
    };
  });
  const metaTitle = title || site.siteMetadata.title;
  const metaDescription = description || site.siteMetadata.description;
  const keywords = site.siteMetadata.keywords;
  const url = `${site.siteMetadata.siteUrl}${pathname}`;
  const ogImage = image || socialImage.childImageSharp.resize;

  return (
    <Helmet
      htmlAttributes={{
        lang: getHrefLangByLocale(locale),
      }}
      titleTemplate={`%s | AMPOS Solutions`}
      title={metaTitle}
      link={hrefLangLinks.concat(link)}
      meta={uniqBy(
        ([] as HelmetProps['meta'][]).concat(meta).concat([
          {
            name: `keywords`,
            content: keywords,
          },
          {
            name: `description`,
            content: metaDescription,
          },
          {
            property: `og:title`,
            content: metaTitle,
          },
          {
            property: `og:description`,
            content: metaDescription,
          },
          {
            property: `og:type`,
            content: `website`,
          },
          {
            property: `og:url`,
            content: url,
          },
          {
            property: `og:site_name`,
            content: `AMPOS - Help People Do Better`,
          },
          {
            property: `og:locale`,
            content: locale,
          },
          {
            name: 'google-site-verification',
            content: '_0EJsFBac_OuqU4fP3qVowZFxxOyq-DkhKUC9z31wq0',
          },
          {
            property: 'og:image',
            content: `${site.siteMetadata.siteUrl}${ogImage.src}`,
          },
          {
            property: 'og:image:alt',
            content: metaTitle,
          },
          {
            property: 'og:image:width',
            content: ogImage.width,
          },
          {
            property: 'og:image:height',
            content: ogImage.height,
          },
          {
            name: `twitter:creator`,
            content: site.siteMetadata.author,
          },
          {
            name: `twitter:title`,
            content: metaTitle,
          },
          {
            name: `twitter:description`,
            content: metaDescription,
          },
          {
            name: `twitter:url`,
            content: url,
          },
          {
            name: 'twitter:card',
            content: 'summary_large_image',
          },
        ]),
        item => item.property || item.name,
      )}
    />
  );
};

SEO.defaultProps = {
  meta: [],
  link: [],
  noIndex: false,
};

SEO.propTypes = {
  description: PropTypes.string.isRequired,
  meta: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.object,
  ]),
  link: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.object,
  ]),
  title: PropTypes.string.isRequired,
  image: PropTypes.object,
  noIndex: PropTypes.bool,
};

export default SEO;
